import { useState } from "react"

import useAppHotkey from "@hooks/useAppHotkey"
import useIsDesktop from "@hooks/useIsDesktop"
import useRouter from "@hooks/useRouter"
import useUser from "@hooks/useUser"

import { faIcon } from "@utils/faIcon"
import replaceSlugs from "@utils/replaceSlugs"

import NavSidebarItem from "@organisms/NavSidebar/components/NavSidebarItem/NavSidebarItem"
import SearchModal from "@organisms/NavSidebar/components/NavSidebarSearchButton/SearchModal/SearchModal"

import { SEARCH_ROUTE } from "@routes/search"

import { NavSidebarSearchButtonProps } from "./NavSidebarSearchButton.types"

const SEARCH_KEYBOARD_HOTKEY = "Mod+K"

export default function NavSidebarSearchButton(props: NavSidebarSearchButtonProps) {
    const { isDisabled = false, _isFocused = false, _isHovered = false, isPressed = false } = props

    const { user } = useUser()

    const [isOpen, setIsOpen] = useState<boolean>(false)

    const isDesktop = useIsDesktop()

    const router = useRouter()

    const onSearchButtonClick = () => {
        if (isDisabled) {
            return null
        } else if (isDesktop) {
            setIsOpen(!isOpen)
        } else {
            router.push(
                replaceSlugs(SEARCH_ROUTE, {
                    service_company_slug: user?.service_company?.slug ?? "undefined",
                }),
            )
        }
    }

    useAppHotkey(
        SEARCH_KEYBOARD_HOTKEY,
        (event) => {
            if (isDisabled) {
                return null
            }
            // Prevent the browser's search from opening
            event.preventDefault()
            onSearchButtonClick()
        },
        {
            // Allow Cmd+K even in form fields since it's a universal search shortcut
            enableOnFormTags: true,
            enabled: !isDisabled && isDesktop,
        },
        [isOpen, isDisabled, isDesktop],
    )

    return (
        <>
            <NavSidebarItem
                label="Search..."
                icon={faIcon({ name: "magnifying-glass", style: "regular" })}
                iconActive={faIcon({ name: "magnifying-glass", style: "solid" })}
                isDisabled={isDisabled}
                _isFocused={_isFocused}
                _isHovered={_isHovered}
                isPressed={isPressed}
                onClick={onSearchButtonClick}
                modifier="hotkeyStack"
                symbolNames={["Mod", "K"]}
            />
            <SearchModal isOpen={isOpen} onOpenChange={setIsOpen} />
        </>
    )
}
